/**
 * ============================================================================
 * PMRecordsTable — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: append-only preventive-maintenance history. Each row shows the
 *          PM type, work description, parts replaced (rendered as a small
 *          unordered list), outcome, engineer and doc-controller co-signer.
 *
 * A11Y: the parts-replaced cell uses a <ul>; an empty list collapses to
 *  an em-dash so the column is never empty for AT.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EquipmentDetail.module.css';
import type {
  PMOutcome,
  PMRecord,
  PMType,
} from './useEquipmentDetailQuery';

const OUTCOME_CLASS: Record<PMOutcome, string> = {
  COMPLETED: styles.outcomePass,
  PARTIAL: styles.outcomeOot,
  DEFERRED: styles.outcomeOot,
  FAILED: styles.outcomeFail,
};

interface PartRow {
  label: string;
}

function partLabel(part: Record<string, unknown>): string {
  // Best-effort label: prefer the part_no + name combo, fall back to the
  // first string value, finally JSON.stringify.
  const partNo = typeof part['part_no'] === 'string' ? part['part_no'] : null;
  const name = typeof part['name'] === 'string' ? part['name'] : null;
  if (partNo && name) return `${partNo} — ${name}`;
  if (name) return name;
  if (partNo) return partNo;
  return JSON.stringify(part);
}

export interface PMRecordsTableProps {
  rows: PMRecord[];
}

export function PMRecordsTable({ rows }: PMRecordsTableProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('equipment.detail.pm.empty', {
          defaultValue: 'No PM records yet.',
        })}
      </p>
    );
  }

  return (
    <table
      className={styles.dataTable}
      aria-label={t('equipment.detail.pm.tableAria', {
        defaultValue: 'PM records',
      })}
    >
      <caption className={styles.srOnly}>
        {t('equipment.detail.pm.tableCaption', {
          defaultValue:
            'Append-only PM history with type, work description, parts replaced, outcome and engineer.',
        })}
      </caption>
      <thead>
        <tr>
          <th scope="col">
            {t('equipment.detail.pm.columns.performedAt', {
              defaultValue: 'Performed at',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.pm.columns.type', { defaultValue: 'Type' })}
          </th>
          <th scope="col">
            {t('equipment.detail.pm.columns.outcome', {
              defaultValue: 'Outcome',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.pm.columns.engineer', {
              defaultValue: 'Engineer',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.pm.columns.workDescription', {
              defaultValue: 'Work description',
            })}
          </th>
          <th scope="col">
            {t('equipment.detail.pm.columns.partsReplaced', {
              defaultValue: 'Parts replaced',
            })}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const parts: PartRow[] = r.parts_replaced.map((p) => ({
            label: partLabel(p),
          }));
          return (
            <tr key={r.pm_id}>
              <td>
                <time dateTime={r.performed_at_utc}>
                  {dateFmt.format(new Date(r.performed_at_utc))}
                </time>
              </td>
              <td>
                {t(`equipment.detail.pm.type.${r.pm_type as PMType}`, {
                  defaultValue: r.pm_type,
                })}
              </td>
              <td>
                <span className={OUTCOME_CLASS[r.outcome]}>
                  {t(`equipment.detail.pm.outcome.${r.outcome}`, {
                    defaultValue: r.outcome,
                  })}
                </span>
              </td>
              <td>{r.engineer}</td>
              <td>{r.work_description}</td>
              <td>
                {parts.length === 0 ? (
                  <span className={styles.subdued}>—</span>
                ) : (
                  <ul className={styles.partsList}>
                    {parts.map((p, i) => (
                      <li key={`${r.pm_id}-${i}`}>{p.label}</li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
